import type { ReactNode } from 'react';

/**
 * O cartão branco do design (borda #E3E8F2, raio 14). Tudo que o dashboard
 * mostra é uma variação dele, então a moldura mora aqui e as variações abaixo
 * só decidem o conteúdo.
 */
export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`border-line-200 rounded-[14px] border bg-white p-4 ${className}`}>
      {children}
    </div>
  );
}

interface StatCardProps {
  label: string;
  /** O número grande. String para caber "—" enquanto não carregou. */
  value: number | string;
  /** Linha de apoio embaixo do número (contexto, não repetição do valor). */
  sub?: string;
  /** Classe de fundo do pontinho ao lado do rótulo (cor da operadora). */
  dot?: string;
}

/** Card de contagem: rótulo com pontinho, número grande e uma linha de apoio. */
export function StatCard({ label, value, sub, dot = 'bg-ink-400' }: StatCardProps) {
  return (
    <Card>
      <div className="text-ink-600 flex items-center gap-1.5 text-[12px] font-bold">
        <span className={`size-2 shrink-0 rounded-full ${dot}`} />
        {label}
      </div>
      <div className="mt-1.5 text-[28px] leading-none font-extrabold">{value}</div>
      {sub ? <div className="text-ink-400 mt-2 text-[11.5px]">{sub}</div> : null}
    </Card>
  );
}

interface PanelProps {
  title: string;
  /** Atalho no canto direito ("ver tudo"), quando a tela tem um destino. */
  action?: { label: string; onClick: () => void };
  children: ReactNode;
}

/** Card com título e um atalho opcional — os dois blocos de baixo do dashboard. */
export function Panel({ title, action, children }: PanelProps) {
  return (
    <Card>
      <div className="mb-2.5 flex items-center justify-between gap-2">
        <div className="text-[14px] font-extrabold">{title}</div>
        {action ? (
          <button
            type="button"
            onClick={action.onClick}
            className="text-navy-800 shrink-0 text-[12px] font-bold"
          >
            {action.label}
          </button>
        ) : null}
      </div>
      {children}
    </Card>
  );
}
